// Shared human-readable rendering for an entity's `visibility` field.
//
// Used by `grid plug` final output (services A.4) and `grid info`
// (A.5) so the same field renders the same way in both places. §23 voice:
// plain, specific, no marketing.
//
// The four runtime visibility modes the CLI's `grid visibility set`
// accepts are private | authenticated | org | link. `public` is in
// the wider EntityVisibility union (inspirations / future tiers) but the
// runtime PATCH route does not accept it; it's described here for completeness.

/** One-line description per visibility mode. */
const DESCRIPTIONS: Record<string, string> = {
  private: 'owner and admins only',
  authenticated: 'any signed-in user',
  org: 'org members only',
  link: 'anyone with the link',
  public: 'anyone, listed publicly',
};

/** Human phrase for a visibility value; falls back to the raw value itself. */
export function visibilityDescription(visibility: string): string {
  return DESCRIPTIONS[visibility] ?? visibility;
}

/**
 * True when an outside visitor can already reach the entity without the owner
 * changing anything. Only `link` and `public` clear that bar; every other mode
 * (private / authenticated / org) gates anonymous visitors, so the
 * "make it shareable" tip is worth showing.
 */
export function isOutsiderReachable(visibility: string): boolean {
  return visibility === 'link' || visibility === 'public';
}

/**
 * QA-13 — the share-moment affordance shown right under the Outlet URL after a
 * successful plug. Returns the block's content lines (no surrounding blank
 * lines; the caller frames the block). The first line always states the
 * current visibility; a second CTA line is added only when the user can take a
 * single step to widen who can reach the entity:
 *
 *   link / public        already reachable by an outside visitor — no CTA.
 *   private              owner+admins only — CTA opens it to the org.
 *   org / authenticated  signed-in gate — CTA opens a public link.
 *
 * The CTA always prints the exact `grid visibility set` command so the
 * user never has to go hunt for it. (`public` is not a valid runtime
 * visibility — the runtime PATCH route accepts only private | authenticated |
 * org | link — so the make-shareable CTA targets `link`, not `public`.)
 */
export function renderPlugVisibilityBlock(
  visibility: string,
  yamlSlug: string,
): string[] {
  const lines = [
    `  Visibility: ${visibility} (${visibilityDescription(visibility)})`,
  ];
  if (isOutsiderReachable(visibility)) {
    return lines;
  }
  if (visibility === 'private') {
    lines.push(
      `  Share with your grid: grid visibility set ${yamlSlug} org`,
    );
  } else {
    lines.push(
      `  Make it shareable: grid visibility set ${yamlSlug} link`,
    );
  }
  return lines;
}
